/**
 * Tabbed collection — builds the tab nav from the rendered panels (same approach as
 * browse-by-activity-tabs.js), loads deferred panels and initialises each panel's Swiper.
 *
 * Moved verbatim out of the inline <script> in sections/tabbed-collection.liquid.
 * The one Liquid interpolation it used is now a data attribute on the section root:
 *
 *   data-tabbed-collection-tabs   marker — every matching element on the page is booted
 */
(function () {
  // The section emits this tag once per instance, so the browser may execute the file
  // more than once on a page. Everything below (including the shopify:section:load
  // registration) runs exactly once; boot() picks up every instance.
  if (window.__tabbedCollectionTabs) return;
  window.__tabbedCollectionTabs = true;

  function initTabs(section) {
    if (!section || section.dataset.tabsInit === 'true') return;
    section.dataset.tabsInit = 'true';

    const tabsNav = section.querySelector('.tabs-nav');
    const contentItems = section.querySelectorAll('.tabs-content__item');
    if (!tabsNav || contentItems.length === 0) return;

    // Build one button per rendered panel so the nav always matches what Liquid output.
    // The first panel in DOM order is the active tab.
    tabsNav.innerHTML = '';
    contentItems.forEach((item, index) => {
      const label = item.getAttribute('data-tab-label') || 'Tab';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tabs-nav__item tabbed-btn' + (index === 0 ? ' is-active' : '');
      btn.setAttribute('data-tab-id', item.id);
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      btn.setAttribute('aria-controls', item.id);
      btn.setAttribute('aria-label', `View ${label} collection`);
      btn.textContent = label;
      tabsNav.appendChild(btn);
    });
    const navButtons = tabsNav.querySelectorAll('button.tabbed-btn');
    const tabLinkContainer = section.querySelector('.tab-link');
    const linkEl = tabLinkContainer ? tabLinkContainer.querySelector('a') : null;

    function updateLink(activeContent) {
      if (!tabLinkContainer || !linkEl) return;
      const linkUrl = activeContent.getAttribute('data-tab-link-url');
      const linkTitle = activeContent.getAttribute('data-tab-link-title');
      if (linkUrl && linkTitle) {
        linkEl.href = linkUrl;
        linkEl.textContent = linkTitle;
        linkEl.setAttribute('aria-label', linkTitle);
        tabLinkContainer.style.display = 'block';
      } else {
        tabLinkContainer.style.display = 'none';
      }
    }

    // Reveal the collection link on failure so a deferred tab is never an empty panel.
    function showDeferredFallback(panel) {
      panel.dataset.deferredState = 'error';
      panel.classList.add('tabs-content__item--error');
      panel.style.minHeight = '';
      const fallback = panel.querySelector('.tabbed-deferred__fallback');
      if (fallback) fallback.classList.remove('visually-hidden');
    }

    // First activation of a deferred tab: fetch the shared deferred-product-grid section.
    // The panel remains hidden until this promise resolves, so the visitor continues to see
    // the existing products instead of a new theme-authored loading treatment.
    function loadDeferredPanel(panel) {
      if (panel.dataset.deferredState === 'loaded') return Promise.resolve(true);
      if (panel._deferredLoadPromise) return panel._deferredLoadPromise;

      const track = panel.querySelector('.tabbed-slider-track');
      const url = panel.getAttribute('data-deferred-collection-url');
      const loader = window.DeferredProductGrid;
      if (!track || !url || !loader) {
        showDeferredFallback(panel);
        return Promise.resolve(false);
      }

      panel.dataset.deferredState = 'loading';
      panel._deferredLoadPromise = loader.fetchCards(url).then((items) => {
        // Match the server-rendered Liquid loop: apply `limit` to collection positions
        // first, then remove unavailable products. The endpoint retains unavailable
        // markers specifically so later products cannot shift into this tab.
        const limit = parseInt(panel.getAttribute('data-deferred-limit'), 10) || (items || []).length;
        const usable = (items || [])
          .slice(0, limit)
          .filter((item) => item.getAttribute('data-product-available') !== 'false')
          .filter((item) => item.querySelector('.card-gallery'));
        if (usable.length === 0) {
          showDeferredFallback(panel);
          return false;
        }

        const fragment = document.createDocumentFragment();
        usable.forEach((item) => {
          item.className = 'swiper-slide tabbed-slider-item';
          fragment.appendChild(item);
        });

        track.innerHTML = '';
        track.appendChild(fragment);
        panel.dataset.deferredState = 'loaded';
        panel.classList.remove('tabs-content__item--error');

        const fallback = panel.querySelector('.tabbed-deferred__fallback');
        if (fallback) fallback.remove();

        // Injected cards re-register their swatches; promo badges self-inject via their
        // existing childList MutationObserver.
        if (window.ProductColorSwatches && typeof window.ProductColorSwatches.refresh === 'function') {
          window.ProductColorSwatches.refresh(panel);
        }

        return true;
      }).catch(() => {
        showDeferredFallback(panel);
        return false;
      }).finally(() => {
        panel._deferredLoadPromise = null;
      });

      return panel._deferredLoadPromise;
    }

    function commitTab(nav, activeContent) {
      navButtons.forEach(n => {
        n.classList.remove('is-active');
        n.setAttribute('aria-selected', 'false');
      });
      contentItems.forEach(c => c.classList.remove('is-active'));

      nav.classList.add('is-active');
      nav.setAttribute('aria-selected', 'true');
      activeContent.classList.add('is-active');
      updateLink(activeContent);
      if (!initTabSwiper(activeContent)) {
        window.loadSwiper().then(() => initTabSwiper(activeContent)).catch(() => {});
      }
    }

    function activateTab(nav) {
      const tabId = nav.getAttribute('data-tab-id');
      const activeContent = section.querySelector('#' + CSS.escape(tabId));
      if (!activeContent) return;

      // Last click wins if the visitor changes their mind while a tab is loading.
      section.dataset.pendingTabId = tabId;

      if (activeContent.hasAttribute('data-deferred-collection-url') && activeContent.dataset.deferredState !== 'loaded') {
        nav.setAttribute('aria-busy', 'true');
        loadDeferredPanel(activeContent).then(() => {
          nav.removeAttribute('aria-busy');
          if (section.dataset.pendingTabId !== tabId) return;
          commitTab(nav, activeContent);
        });
      } else {
        commitTab(nav, activeContent);
      }
    }

    // <button> covers pointer, touch and keyboard (Enter/Space) activation.
    navButtons.forEach(nav => {
      nav.addEventListener('click', (e) => {
        e.preventDefault();
        activateTab(nav);
      });
    });

    // Theme editor: selecting a tab block switches to its panel.
    section._activateTabPanel = (panelId) => {
      const nav = Array.from(navButtons).find(b => b.getAttribute('data-tab-id') === panelId);
      if (nav) activateTab(nav);
    };

    // Liquid may mark more than one panel active; keep only the first.
    const firstContent = contentItems[0];
    contentItems.forEach(c => c.classList.toggle('is-active', c === firstContent));

    if (firstContent.hasAttribute('data-deferred-collection-url') && firstContent.dataset.deferredState !== 'loaded') {
      // First panel arrived empty (e.g. an earlier block is disabled) — load it now.
      activateTab(navButtons[0]);
    } else {
      updateLink(firstContent);

      // Lazy-initialize Swiper for the first active tab using IntersectionObserver.
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            // Try without the library first — desktop grid mode returns true and never
            // downloads Swiper. Disconnect only once the init actually took.
            if (initTabSwiper(firstContent)) {
              observer.disconnect();
              return;
            }
            window.loadSwiper().then(() => {
              if (initTabSwiper(firstContent)) observer.disconnect();
            }).catch(() => {});
          });
        }, { rootMargin: '200px 0px' });
        observer.observe(section);
      } else if (!initTabSwiper(firstContent)) {
        window.loadSwiper().then(() => initTabSwiper(firstContent)).catch(() => {});
      }
    }
  }

  function initTabSwiper(container) {
    const swiperContainer = container.querySelector('.tabbed-swiper');
    if (!swiperContainer) return false;
    if (swiperContainer.swiper) return true; // Already initialized
    // Never initialize an empty deferred panel.
    if (container.hasAttribute('data-deferred-collection-url') && container.dataset.deferredState !== 'loaded') return false;

    const slides = swiperContainer.querySelectorAll('.tabbed-slider-item');
    if (slides.length === 0) return false;

    const isMobile = window.innerWidth < 750;

    if (!isMobile && slides.length <= 3) {
      swiperContainer.classList.add('tabbed-grid-mode');
      swiperContainer.classList.remove('swiper');
      const wrapper = swiperContainer.querySelector('.tabbed-slider-track');
      if(wrapper) wrapper.classList.remove('swiper-wrapper');
      slides.forEach(s => s.classList.remove('swiper-slide'));
      return true;
    }

    swiperContainer.classList.add('swiper');
    const wrapper = swiperContainer.querySelector('.tabbed-slider-track');
    if(wrapper) wrapper.classList.add('swiper-wrapper');
    slides.forEach(s => s.classList.add('swiper-slide'));

    const gap = 8;
    const hasEnoughForLoop = slides.length >= 4;

    // Library not in yet — the caller fetches it via loadSwiper() and calls back.
    if (!window.Swiper) return false;

    new Swiper(swiperContainer, {
      spaceBetween: gap,
      observer: false,
      observeParents: false,
      resizeObserver: false,
      navigation: {
        nextEl: container.querySelector('.tabbed-slider-next'),
        prevEl: container.querySelector('.tabbed-slider-prev'),
      },
      watchSlidesProgress: true,
      preventInteractionOnTransition: true,
      pagination: {
        el: container.querySelector('.swiper-pagination'),
        type: 'progressbar',
      },
      breakpoints: {
        0: {
          allowTouchMove: true,
          slidesPerView: 'auto',
          centeredSlides: false,
          loop: false,
          slidesOffsetBefore: 16,
          slidesOffsetAfter: 16
        },
        750: {
          allowTouchMove: false,
          slidesPerView: 'auto',
          centeredSlides: true,
          loop: hasEnoughForLoop,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0
        }
      },

      on: {
        init: function () {
          const isMobile = window.innerWidth <= 749;
          const arrows = container.querySelector('slideshow-arrows');
          const progressbar = container.querySelector('.swiper-pagination');
          if (isMobile) {
            if (arrows) arrows.style.display = 'none';
            if (progressbar) progressbar.style.display = 'block';
          } else {
            if (arrows) arrows.style.display = 'block';
            if (progressbar) progressbar.style.display = 'none';
          }
          requestAnimationFrame(setArrowPosition);
        },
        resize: function () {
          const isMobile = window.innerWidth <= 749;
          const arrows = container.querySelector('slideshow-arrows');
          const progressbar = container.querySelector('.swiper-pagination');
          if (isMobile) {
            if (arrows) arrows.style.display = 'none';
            if (progressbar) progressbar.style.display = 'block';
          } else {
            if (arrows) arrows.style.display = 'block';
            if (progressbar) progressbar.style.display = 'none';
          }
          requestAnimationFrame(setArrowPosition);
        }
      }
    });

    // Position arrows asynchronously at the vertical center of the product card image
    function setArrowPosition() {
      const firstImg = container.querySelector('.image-block__image, .product-media__image');
      if (firstImg) {
        const imgH = firstImg.offsetHeight;
        if (imgH > 0) {
          container.style.setProperty('--arrow-top', (imgH / 2) + 'px');
        }
      }
    }

    return true;
  }

  // Boot every section root in `scope` (the document on load, the re-injected
  // section element in the theme editor). initTabs latches per root.
  function boot(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches('[data-tabbed-collection-tabs]')) initTabs(scope);
    scope.querySelectorAll('[data-tabbed-collection-tabs]').forEach((root) => initTabs(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      boot(event.target);
    });
    document.addEventListener('shopify:block:select', function (event) {
      const panel = event.target.closest && event.target.closest('.tabs-content__item');
      const section = panel && panel.closest('[data-tabbed-collection-tabs]');
      if (section && section._activateTabPanel) section._activateTabPanel(panel.id);
    });
  }
})();
